import type { ReactNode } from 'react';

import { Box, Text } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Image, type StaticImageData } from 'Components/Image';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Wrapper = styled.div<{ columns: string; cardColor?: string }>`
  margin-top: ${legacyTheme.spacing.s24};
  max-width: 1400px;

  border-top: 1px solid;
  border-left: 1px solid;

  background-color: ${({ cardColor }) => cardColor};

  ${legacyTheme.breakpoints.up('sm')} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: grid;
    grid-template-columns: ${({ columns }) => columns};
  }
`;

const Card = styled.div<{ clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;

  border-right: 1px solid;
  border-bottom: 1px solid;

  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'auto')};
`;

const CardTitle = styled.div`
  display: flex;

  margin: 0 auto 4px auto;

  gap: ${legacyTheme.spacing.s8};
`;

type ImageCardCollectionProps = {
  columns?: string;
  fixedHeight?: boolean;
  onClick?: () => void;
  cardColor?: string;
  list: {
    title: string;
    subTitle: string;
    description: string;
    images: {
      mobile: StaticImageData;
      desktop: StaticImageData;
      alt: string;
    };
    icon?: ReactNode;
    footerText: string;
  }[];
  titleTag?: 'h3' | 'h4';
};

export const ImageCardCollection = ({
  columns = '1fr 1fr 1fr',
  list,
  cardColor,
  onClick,
  fixedHeight = false,
  titleTag = 'h4',
}: ImageCardCollectionProps) => {
  const { isDesktop } = useResponsiveVariant();

  return (
    <Wrapper cardColor={cardColor} columns={columns}>
      {list.map(({ description, title, icon, subTitle, images, footerText }) => (
        <Card key={title} clickable={Boolean(onClick)} onClick={() => onClick?.()}>
          <Image
            alt={images.alt}
            src={isDesktop ? images.desktop : images.mobile}
            style={{
              width: '100%',
              height: fixedHeight && isDesktop ? '400px' : 'auto',
              objectFit: 'cover',
            }}
          />

          <Box
            borderColor="neutral.900"
            borderTop="1px"
            display="flex"
            flexDirection="column"
            flexGrow={1}
            gap="4x"
            paddingBottom="4x"
            paddingTop="6x"
          >
            <Box display="flex" flexDirection="column">
              <CardTitle>
                <Text as={titleTag} color="neutral.900" variant="heading6">
                  {title}
                </Text>
                {icon}
              </CardTitle>
              <Text textAlign="center" variant="labelMd">
                {subTitle}
              </Text>
            </Box>
            <Box display="flex" flexDirection="column" flexGrow={1} gap="4x" paddingX="6x">
              <Text align="center" color="neutral.800" flexGrow="1" variant="bodyLg">
                {description}
              </Text>
              {footerText && (
                <Text color="neutral.800" variant="bodyLg">
                  {footerText}
                </Text>
              )}
            </Box>
          </Box>
        </Card>
      ))}
    </Wrapper>
  );
};
